import { Component, ElementRef, OnDestroy, ViewChild, ViewEncapsulation } from '@angular/core';
import { GridReadyEvent } from '@ag-grid-community/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { DialogService } from 'primeng/dynamicdialog';
import _ from 'lodash';

import { DialogBaseComponent, DialogParams } from 'app/shared/dialog-base.component';
import { DomainService } from 'app/shared/domain.service';
import { AgFns, GridOptionsExt, ISortModel } from 'app/shared/ag-fns';
import { ImageFns } from 'app/shared/image/image-fns';
import { ImageMatchFns, ImageMatchResult } from 'app/shared/image/image-match-fns';
import { Image, Manufacturer, ProductType, ProductTypeImage } from 'app/model/entities/entity-model';
import { EntityFinderDialog } from 'app/shared/entity-finder.dialog';
import { ProductTypeFinderDialogComponent } from './product-type-finder-dialog.component';
import { UtilFns } from 'app/shared/util-fns';

export interface BulkImageUploadDialogParams extends DialogParams {}

@Component({
  selector: 'app-bulk-image-upload-dialog',
  templateUrl: './bulk-image-upload-dialog.component.html',
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class BulkImageUploadDialogComponent extends DialogBaseComponent<BulkImageUploadDialogParams> implements OnDestroy {
  @ViewChild('filePicker') filePicker: ElementRef<HTMLInputElement>;

  allProductTypes: ProductType[] = [];
  manufacturers: Manufacturer[] = [];
  selectedManufacturer: Manufacturer | null = null;

  matchResults: ImageMatchResult[] = [];
  gridOptions!: GridOptionsExt;

  matchedCount = 0;
  unmatchedCount = 0;
  skippedCount = 0;
  totalCount = 0;

  setAsPrimary = true;
  skipExisting = true;
  showHelp = false;

  processingStatus = '';

  private blobUrls: string[] = [];

  constructor(config: DynamicDialogConfig<BulkImageUploadDialogParams>, dialogRef: DynamicDialogRef, domainService: DomainService) {
    super(config, dialogRef, domainService);
    this.config.header = 'Bulk Image Upload';
    this.config.closable = true;
    this.prepare();
  }

  getPngDialogService(): DialogService {
    return this.domainService.pngDialogService;
  }

  async prepare() {
    await this.wrapBusy(async () => {
      this.allProductTypes = await this.dbQueryService.getProductTypesForImageMatching();

      const manufMap = new Map<number, Manufacturer>();
      for (const pt of this.allProductTypes) {
        if (pt.manufacturer) {
          manufMap.set(pt.manufacturer.id, pt.manufacturer);
        }
      }
      this.manufacturers = _.sortBy(Array.from(manufMap.values()), (m) => m.name);

      this.gridOptions = AgFns.initGridOptions(this, {
        onGridReady: this.onGridReady.bind(this),
        getRowId: (params: any) => (params.data?.file?.name ?? 'none').toLowerCase(),
      });

      this.isPageReady = true;
    });
  }

  onGridReady(event: GridReadyEvent) {
    const colDefs = [
      {
        headerName: '',
        colId: 'thumbnail',
        cellRenderer: (params: any) => {
          if (!params.data?._thumbnailUrl) return '';
          return `<img src="${params.data._thumbnailUrl}" style="height:36px; width:36px; object-fit:contain;" />`;
        },
        sortable: false,
        filter: false,
        ...AgFns.fixWidth(50),
      },
      {
        headerName: 'Filename',
        colId: 'fileName',
        valueGetter: (p: any) => p.data?.file?.name ?? '',
        filter: 'agTextColumnFilter',
        ...AgFns.flexWidth(180),
      },
      {
        headerName: 'Match Status',
        colId: 'matchStatus',
        valueGetter: (p: any) => {
          const r = p.data as ImageMatchResult;
          if (!r) return '';
          if (r._skip) return 'Skip (existing)';
          if (r._processed) return 'Uploaded';
          if (!r.matchedProductType) return 'No Match';
          return r.matchedBy === 'manual' ? 'Manual' : `Matched (${r.matchedBy})`;
        },
        cellStyle: (p: any) => {
          const r = p.data as ImageMatchResult;
          if (r?._processed) return { color: 'var(--p-green-500, green)' };
          if (!r?.matchedProductType && !r?._skip) return { color: 'var(--p-red-500, #ef4444)', fontWeight: 'bold' };
          if (r?._skip) return { color: 'var(--p-text-muted-color, gray)' };
          return null;
        },
        filter: 'agTextColumnFilter',
        ...AgFns.fixWidth(160),
      },
      {
        headerName: 'Manufacturer',
        colId: 'manufacturer',
        valueGetter: (p: any) => p.data?.matchedProductType?.manufacturer?.name ?? '',
        filter: 'agTextColumnFilter',
        ...AgFns.flexWidth(140),
      },
      {
        headerName: 'SKU (Style)',
        colId: 'style',
        valueGetter: (p: any) => p.data?.matchedProductType?.style ?? '',
        filter: 'agTextColumnFilter',
        ...AgFns.flexWidth(120),
      },
      {
        headerName: 'Description',
        colId: 'description',
        valueGetter: (p: any) => p.data?.matchedProductType?.description ?? '',
        filter: 'agTextColumnFilter',
        ...AgFns.flexWidth(160),
      },
      {
        headerName: 'Existing',
        colId: 'existingImageCount',
        valueGetter: (p: any) => p.data?.existingImageCount ?? 0,
        ...AgFns.fixWidth(90),
      },
      {
        ...AgFns.createButtonProps('', this.assignManually.bind(this), {
          label: 'Assign',
          canDisplay: (r: ImageMatchResult) => !r.matchedProductType && !r._skip && !r._processed,
        }),
        ...AgFns.fixWidth(90),
      },
      {
        ...AgFns.createButtonProps('', this.clearMatch.bind(this), {
          label: 'Clear',
          canDisplay: (r: ImageMatchResult) => !!r.matchedProductType && !r._processed,
        }),
        ...AgFns.fixWidth(80),
      },
    ];

    const sortModel = [{ colId: 'fileName', sort: 'asc' }] as ISortModel;
    AgFns.initGrid(event, colDefs, sortModel);
  }

  selectFiles() {
    this.filePicker?.nativeElement?.click();
  }

  async onFilesSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    const picked = Array.from(input.files).filter((f) => f.type.startsWith('image/'));
    if (picked.length === 0) return;

    const existingNames = new Set(this.matchResults.map((r) => r.file.name.toLowerCase()));
    const seenInBatch = new Set<string>();
    const duplicates: string[] = [];
    const files: File[] = [];

    for (const f of picked) {
      const key = f.name.toLowerCase();
      if (existingNames.has(key) || seenInBatch.has(key)) {
        duplicates.push(f.name);
        continue;
      }
      seenInBatch.add(key);
      files.push(f);
    }

    if (duplicates.length > 0) {
      const sample = duplicates.slice(0, 3).join(', ');
      const more = duplicates.length > 3 ? `, +${duplicates.length - 3} more` : '';
      this.toastr.info(`Skipped ${duplicates.length} duplicate file(s): ${sample}${more}`);
    }

    if (files.length === 0) {
      input.value = '';
      return;
    }

    await this.wrapBusy(async () => {
      const newResults: ImageMatchResult[] = files.map((file) => {
        const url = URL.createObjectURL(file);
        this.blobUrls.push(url);
        return {
          file,
          matchedProductType: null,
          matchedBy: null,
          existingImageCount: 0,
          _thumbnailUrl: url,
        } as ImageMatchResult;
      });

      this.matchResults = [...this.matchResults, ...newResults];
      this.rematchAll();
    });

    input.value = '';
  }

  onManufacturerChanged() {
    this.rematchAll();
  }

  private rematchAll() {
    const scopedPts = this.getScopedProductTypes();
    const filesToMatch: File[] = [];
    const manualResults: ImageMatchResult[] = [];

    // Preserve manual assignments; re-match everything else
    for (const r of this.matchResults) {
      if (r._processed) {
        // Already saved — don't re-match
        continue;
      }
      if (r.matchedBy === 'manual') {
        manualResults.push(r);
      } else {
        filesToMatch.push(r.file);
      }
    }

    const freshMatches = ImageMatchFns.matchFiles(filesToMatch, scopedPts);

    // Carry over blob URLs from existing results
    for (const fresh of freshMatches) {
      const existing = this.matchResults.find((r) => r.file === fresh.file);
      if (existing) {
        fresh._thumbnailUrl = existing._thumbnailUrl;
      }
    }

    // Apply defaults
    for (const r of freshMatches) {
      r._setAsPrimary = this.setAsPrimary;
      if (this.skipExisting && r.existingImageCount > 0) {
        r._skip = true;
      }
    }

    // Rebuild: processed + manual + fresh
    const processed = this.matchResults.filter((r) => r._processed);
    this.matchResults = [...processed, ...manualResults, ...freshMatches];
    this.updateStats();
    AgFns.refreshGrid(this.gridOptions, this.matchResults);
  }

  private getScopedProductTypes(): ProductType[] {
    if (this.selectedManufacturer) {
      return this.allProductTypes.filter((pt) => pt.manufacturer?.id === this.selectedManufacturer.id);
    }
    return this.allProductTypes;
  }

  async assignManually(result: ImageMatchResult) {
    const r = await EntityFinderDialog.open(ProductTypeFinderDialogComponent, this, {});
    if (r?.length) {
      const pt = r[0];
      result.matchedProductType = pt;
      result.matchedBy = 'manual';
      result.existingImageCount = pt.productTypeImages?.filter((pti) => !pti.isThumbnail).length ?? 0;
      result._setAsPrimary = this.setAsPrimary;
      if (this.skipExisting && result.existingImageCount > 0) {
        result._skip = true;
      }
      this.updateStats();
      AgFns.refreshGrid(this.gridOptions, this.matchResults);
    }
  }

  clearMatch(result: ImageMatchResult) {
    result.matchedProductType = null;
    result.matchedBy = null;
    result.existingImageCount = 0;
    result._skip = false;
    this.updateStats();
    AgFns.refreshGrid(this.gridOptions, this.matchResults);
  }

  removeUnmatched() {
    const unmatched = this.matchResults.filter((r) => !r.matchedProductType && !r._skip && !r._processed);
    for (const r of unmatched) {
      this.revokeUrl(r);
    }
    this.matchResults = this.matchResults.filter((r) => r.matchedProductType || r._skip || r._processed);
    this.updateStats();
    AgFns.refreshGrid(this.gridOptions, this.matchResults);
  }

  clearAll() {
    for (const r of this.matchResults) {
      this.revokeUrl(r);
    }
    this.matchResults = [];
    this.updateStats();
    AgFns.refreshGrid(this.gridOptions, this.matchResults);
  }

  async saveAll() {
    const toProcess = this.matchResults.filter((r) => r.matchedProductType && !r._skip && !r._processed);

    if (toProcess.length === 0) {
      await this.dialogService.yesNoDialog('Nothing to Upload', 'No matched images to process.');
      return;
    }

    const ok = await this.dialogService.yesNoDialog('Confirm Upload', `Upload ${toProcess.length} image(s) to their matched Product Types?`);
    if (!ok) return;

    await this.wrapBusy(async () => {
      let successCount = 0;

      for (let i = 0; i < toProcess.length; i++) {
        const result = toProcess[i];
        this.processingStatus = `Processing ${i + 1} of ${toProcess.length}...`;
        await UtilFns.wait(1);

        try {
          // Resize to standard
          const appImage = await ImageFns.resizeFileToStandard(result.file);
          const imageEntity = await ImageFns.makeImageEntity(appImage, this.uow);

          // Clear existing primary if needed
          if (result._setAsPrimary) {
            const existingPrimaries = result.matchedProductType.productTypeImages.filter((pti) => pti.isPrimary && !pti.isThumbnail);
            for (const ep of existingPrimaries) {
              ep.isPrimary = false;
            }
          }

          // Create join entity
          this.uow.createEntity(ProductTypeImage, {
            productTypeId: result.matchedProductType.id,
            imageId: imageEntity.id,
            isPrimary: result._setAsPrimary ?? false,
            isThumbnail: false,
          });

          // Create thumbnail
          const thumbImage = await ImageFns.resizeFileToThumbnail(result.file);
          const thumbEntity = await ImageFns.makeImageEntity(thumbImage, this.uow);
          this.uow.createEntity(ProductTypeImage, {
            productTypeId: result.matchedProductType.id,
            imageId: thumbEntity.id,
            isPrimary: result._setAsPrimary ?? false,
            isThumbnail: true,
          });

          result._processed = true;
          successCount++;
        } catch (err) {
          console.error(`Failed to process ${result.file.name}:`, err);
        }
      }

      this.processingStatus = '';

      const saved = await this.dbSaveService.saveChanges();
      if (saved) {
        this.toastr.success(`${successCount} image(s) uploaded successfully`);
        this.updateStats();
        AgFns.refreshGrid(this.gridOptions, this.matchResults);
      }
    });
  }

  onClose() {
    this.dialogRef.close(this.matchResults.some((r) => r._processed));
  }

  ngOnDestroy() {
    for (const url of this.blobUrls) {
      URL.revokeObjectURL(url);
    }
    this.blobUrls = [];
  }

  private updateStats() {
    this.totalCount = this.matchResults.length;
    this.matchedCount = this.matchResults.filter((r) => r.matchedProductType && !r._skip && !r._processed).length;
    this.skippedCount = this.matchResults.filter((r) => r._skip).length;
    this.unmatchedCount = this.matchResults.filter((r) => !r.matchedProductType && !r._skip && !r._processed).length;
  }

  private revokeUrl(r: ImageMatchResult) {
    const url = r._thumbnailUrl;
    if (url) {
      URL.revokeObjectURL(url);
      _.remove(this.blobUrls, (u) => u === url);
    }
  }
}
